import React from "react";
import { useTranslation } from "react-i18next";
import UserSettingsService, { SingleWorkspaceSettingsPaths } from "src/services/userSettingsService";
import { makeStyles } from "tss-react/mui";

import { Box, BoxProps, Collapse, Typography } from "@mui/material";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";

const useStyles = makeStyles()((theme) => ({
    summary: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    summaryToggle: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
    },
    summaryAction: {
        marginLeft: "auto",
    },
    details: {
        paddingBottom: theme.spacing(4),
    },
    detailsDisabled: {
        pointerEvents: "none",
        opacity: 0.5,
    },
    title: {
        display: "flex",
        alignItems: "flex-end",
        gap: theme.spacing(2),
    },
}));

interface CollapsibleGroupContextType {
    expandAll: boolean | null;
    setExpandAll: React.Dispatch<React.SetStateAction<boolean | null>>;
}
export const CollapsibleGroupContext = React.createContext<CollapsibleGroupContextType>({
    expandAll: null,
    setExpandAll: () => null,
});
type CollapsibleGroupProps = React.PropsWithChildren<{
    initialValue?: boolean | null;
}>;
export const CollapsibleGroupProvider = (props: CollapsibleGroupProps) => {
    const [expandAll, setExpandAll] = React.useState<boolean | null>(props.initialValue || null);

    return (
        <CollapsibleGroupContext.Provider value={{ expandAll, setExpandAll }}>{props.children}</CollapsibleGroupContext.Provider>
    );
};

type CollapsibleContextType = {
    open: boolean;
    disabled: boolean;
    setOpen: (open: boolean) => void;
};
const CollapsibleContext = React.createContext<CollapsibleContextType>({
    open: false,
    disabled: false,
    setOpen: () => null,
});
type CollapsibleProviderType = React.PropsWithChildren<{
    initialValue?: boolean;
    onChange?: (open: boolean) => void;
    disabled?: boolean;
}>;
export const CollapsibleProvider = ({ onChange, ...props }: CollapsibleProviderType) => {
    const [userOpen, setUserOpen] = React.useState<boolean | null>(null);
    const { expandAll, setExpandAll } = React.useContext(CollapsibleGroupContext);
    const [seenExpandAll, setSeenExpandAll] = React.useState(expandAll);

    // Expand all / collapse all overrides whatever was picked for this section before it. When `expandAll` falls back
    // to null (another section toggled on its own), a section keeps the state expand all gave it: its `initialValue`
    // is read from storage when the section last rendered, which can predate expand all, since sections do not
    // re-render when the group changes.
    if (expandAll !== seenExpandAll) {
        setSeenExpandAll(expandAll);
        setUserOpen(expandAll === null ? (userOpen ?? seenExpandAll) : null);
    }

    const open = userOpen ?? expandAll ?? !!props.initialValue;

    const onOpen = (value: boolean) => {
        setUserOpen(value);
        if (onChange) {
            onChange(value);
            setExpandAll(null);
        }
    };

    // Depends on the callback rather than the whole props object, which is a new object on every redraw. With
    // "expand all" active that re-ran the effect constantly, and each run wrote the section state to storage -
    // on the provisioning page, a burst of writes per keystroke across every section.
    React.useEffect(() => {
        if (expandAll === null) {
            return;
        }

        onChange?.(expandAll);
    }, [expandAll, onChange]);

    return (
        <CollapsibleContext.Provider value={{ open, setOpen: onOpen, disabled: props.disabled || false }}>
            <Box>{props.children}</Box>
        </CollapsibleContext.Provider>
    );
};

type CollapsibleSectionSummaryProps = React.PropsWithChildren<BoxProps> & {
    action?: React.ReactNode;
};
export const CollapsibleSectionSummary = ({ children, action, className, ...props }: CollapsibleSectionSummaryProps) => {
    const { t } = useTranslation(["common"]);
    const context = React.useContext(CollapsibleContext);
    const { classes, cx } = useStyles();

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.noContext"));
    }

    return (
        <Box className={cx(classes.summary, className)} {...props}>
            <Box className={classes.summaryToggle} onClick={() => context.setOpen(!context.open)}>
                <ExpandToggleButton expanded={context.open} />
                {children}
            </Box>
            {action ? <Box className={classes.summaryAction}>{action}</Box> : null}
        </Box>
    );
};

export const CollapsibleSectionDetails = (
    props: React.PropsWithChildren<{
        error?: string;
    }>,
) => {
    const context = React.useContext(CollapsibleContext);
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common"]);

    if (!context) {
        throw new Error(t("common:error.collapsibleWrapper.useInsideContext"));
    }

    return (
        <Collapse in={context.open} collapsedSize={0}>
            {props.error ? (
                <Typography color="error" variant="body2">
                    {props.error}
                </Typography>
            ) : null}
            <Box className={cx(classes.details, context.disabled && classes.detailsDisabled)}>{props.children}</Box>
        </Collapse>
    );
};

type CollapsibleSectionTitleProps = {
    error?: string;
};
// Deliberately has no size or weight options, so a section heading looks the same on every page.
export const CollapsibleSectionTitle = (props: React.PropsWithChildren<CollapsibleSectionTitleProps>) => {
    const { classes } = useStyles();

    return (
        <Box className={classes.title}>
            <Typography variant="subtitle1" fontWeight={600}>
                {props.children}
            </Typography>
            {props.error ? (
                <Typography variant="caption" color="error">
                    {props.error}
                </Typography>
            ) : null}
        </Box>
    );
};

export const useCollapsiblePersistence = (storageKey: SingleWorkspaceSettingsPaths, sectionkey: string) => {
    const localStorageValue = () => UserSettingsService.getProvisioningPageCollapsedSections();
    const onChange = (open: boolean) => {
        const collapsedSection = localStorageValue();
        collapsedSection[sectionkey] = open;
        UserSettingsService.setWorkspaceValue(storageKey, collapsedSection);
    };
    return { value: localStorageValue()[sectionkey], onChange };
};
